import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  Button,
  useTheme,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import { 
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { API_URL, createAxiosInstance } from '../../../api';

const RenewCourseTab = React.memo(() => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.main.userId);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper: determine if a course is eligible for renewal
  const isCourseEligibleForRenewal = (course) => {
    // If backend has already marked it expired, always allow renewal
    if (course.isExpire) return true;

    if (!course.expirationDate) return false;

    const expirationDate = new Date(course.expirationDate);
    if (isNaN(expirationDate.getTime())) return false;

    const now = new Date();
    const diffMs = expirationDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    // Expired or within N days of expiring
    const RENEW_THRESHOLD_DAYS = 7;
    return diffDays <= 0 || diffDays <= RENEW_THRESHOLD_DAYS;
  };

  // Fetch tutor's courses and filter to those that are expired or close to expiry
  const fetchCourses = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const axiosInstance = createAxiosInstance();

      // Fetch active and expired courses separately
      const [activeRes, expiredRes] = await Promise.all([
        axiosInstance.get(`${API_URL}/api/courses/coursesUser/${userId}/false`),
        axiosInstance.get(`${API_URL}/api/courses/coursesUser/${userId}/true`)
      ]);

      const allCourses = [
        ...(activeRes.data || []),
        ...(expiredRes.data || [])
      ];

      // Keep only courses that are expired or close to expiry
      const eligibleCourses = allCourses.filter(isCourseEligibleForRenewal);

      setCourses(eligibleCourses);
      
    } catch (error) {
      console.error('Error fetching courses for renewal:', error);
      if (error.response?.status >= 500) {
        setError('Failed to load courses. Please try again.');
      } else {
        setCourses([]);
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleRenewCourse = (courseId) => {
    if (!courseId) return;
    // Navigate to the old per-course renew flow
    navigate(`/course/renew/${courseId}`);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        height: '400px',
        gap: 2
      }}>
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading your courses for renewal...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Renew Course
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a course advert below to renew its advertising period.
        </Typography>
      </Box>

      {courses.length === 0 ? (
        <Card
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            border: '2px dashed #e2e8f0'
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                gap: 2
              }}
            >
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                No Courses Available to Renew
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                You currently have no courses. Create a course first, then you can renew its advertising period here.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2.5}>
          {courses.map((course) => (
            <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={course._id || course.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Course
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {course.name || 'Untitled Course'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {course.subject || 'Subject not set'} • {course.level || 'Level not set'}
                  </Typography>
                  {course.date && Array.isArray(course.date) && course.date.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Date:</strong>{' '}
                      {course.date
                        .map((d) => (d ? new Date(d).toLocaleDateString('en-UK') : null))
                        .filter(Boolean)
                        .join(' - ')}
                    </Typography>
                  )}
                </CardContent>
                {/* Renew action temporarily disabled by request */}
                {/* <Box sx={{ p: 2, pt: 0, textAlign: 'right' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => handleRenewCourse(course._id || course.id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Renew Course
                  </Button>
                </Box> */}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
});

RenewCourseTab.displayName = 'RenewCourseTab';

export default RenewCourseTab;
